/*
  Clamps a long product description to a fixed height and reveals the rest
  behind a "View more" button.

  The clamp is applied here rather than in Liquid so the full description is
  always present in the DOM — search engines index all of it, and a visitor
  without JS gets the complete text instead of a truncated block.

  The button only appears when the content is genuinely taller than the clamp,
  so short descriptions render exactly as they did before.
*/
if (!customElements.get('collapsible-description')) {
  customElements.define(
    'collapsible-description',
    class CollapsibleDescription extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-description-content]');
        this.button = this.querySelector('[data-description-toggle]');
        if (!this.content || !this.button) return;

        this.labelMore = this.querySelector('[data-label-more]');
        this.labelLess = this.querySelector('[data-label-less]');
        this.collapsedHeight = parseInt(this.dataset.collapsedHeight, 10) || 220;
        this.expanded = false;

        this.onClick = this.toggle.bind(this);
        this.button.addEventListener('click', this.onClick);

        // Reflow changes how many lines the text takes, so the clamp may become
        // necessary or unnecessary at a different width.
        this.onResize = this.debounce(this.evaluate.bind(this), 150);
        window.addEventListener('resize', this.onResize);

        this.evaluate();

        // A late webfont can change the text height after the first measurement.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.evaluate());
        }
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        window.removeEventListener('resize', this.onResize);
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      /*
        scrollHeight reports the full content height even while the element is
        clamped, so this stays accurate in both states.

        The 40px margin stops a description that only just exceeds the clamp from
        getting a button that reveals a single extra line.
      */
      evaluate() {
        const overflows = this.content.scrollHeight > this.collapsedHeight + 40;

        // The clamp is the CSS default, so this releases it rather than applying
        // it. That keeps the common case — a long description — shift-free.
        this.classList.toggle('is-not-collapsible', !overflows);
        this.button.hidden = !overflows;

        this.setExpanded(overflows ? this.expanded : false);
      }

      setExpanded(expanded) {
        this.expanded = expanded;
        this.classList.toggle('is-expanded', expanded);
        this.button.setAttribute('aria-expanded', String(expanded));
        if (this.labelMore) this.labelMore.hidden = expanded;
        if (this.labelLess) this.labelLess.hidden = !expanded;
      }

      toggle() {
        const collapsing = this.expanded;
        this.setExpanded(!this.expanded);

        // Collapsing can leave the reader below the description, looking at
        // whatever followed it. Pull the top back into view when that happens.
        if (collapsing) {
          const top = this.getBoundingClientRect().top;
          if (top < 0) {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    }
  );
}
